<template>
  <div ref="nav" :class="`pdl-tabs pdl-tabs--${tabPosition}`">
    <div v-if="tabPosition === 'bottom'" class="pdl-tabs__content">
      <!-- @slot Slot for content. -->
      <slot></slot>
    </div>
    <div :class="[`pdl-tabs__header is-${tabPosition}`, stickyClass]">
      <!-- @slot Slot for pre navbar content -->
      <slot name="pre-nav" />

      <tab-nav :on-tab-click="handleTabClick" :panes="panes" :alignment="alignment" :tab-position="tabPosition" />

      <!-- @slot Slot for post navbar content -->
      <slot name="post-nav" />
    </div>
    <div ref="tabsContent" v-if="tabPosition !== 'bottom'" class="pdl-tabs__content">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import TabNav from './TabNav.vue';
import {computed} from 'vue';
let resizeObserver;

export default {
  name: 'PdlTabs',
  components: {
    TabNav,
  },
  provide() {
    return {
      createTab: (tab) => {
        const panesLength = this.allPanes.push(tab);
        this.paneIndex = panesLength;

        if (this.modelValue === tab.tabName || panesLength === 1) {
          this.activeTabName = tab.tabName;
        }
      },
      restoreTab: (tabName) => {
        const index = this.allPanes.findIndex((tab) => tab.tabName === tabName);
        if (index === -1) return;
        this.allPanes[index].isRemoved = false;
        let newActiveTab;
        if (this.isInit) {
          newActiveTab = this.setInitialOrFirstTabActive();
        } else if (!this.activeTabName || this.activeTabName === tabName) {
          newActiveTab = this.allPanes[index].tabName;
        } else {
          newActiveTab = this.activeTabName;
        }
        this.switchTab(newActiveTab);
      },
      removeTab: (tabName) => {
        const index = this.panes.findIndex((tab) => tab.tabName === tabName);
        if (index === -1) return;
        this.panes[index].isRemoved = true;
        let newActiveTab;
        if (this.activeTabName === tabName) {
          if (this.isInit) {
            newActiveTab = this.setInitialOrFirstTabActive();
          } else {
            newActiveTab =
              this.panes[index - 1]?.tabName || this.panes[index]?.tabName || this.panes[index + 1]?.tabName || '';
          }
          this.switchTab(newActiveTab);
        }
      },
      switchTab: this.switchTab,
      paneIndex: computed(() => this.paneIndex),
      activeTabName: computed(() => this.activeTabName),
      navWidth: computed(() => this.navWidth),
    };
  },
  props: {
    /**
     * Binding value, name of the selected tab.
     */
    modelValue: {
      type: String,
      default: '',
    },
    /**
     * Horizontal alignment of tabs (`start`/`center`/`end`/`stretch`).
     */
    alignment: {
      type: String,
      default: 'start',
      validator(value) {
        return ['start', 'center', 'end', 'stretch'].includes(value);
      },
    },
    /**
     * Position of tabs (`top`/`left`/`right`/`bottom`).
     */
    tabPosition: {
      type: String,
      default: 'top',
      validator(value) {
        return ['top', 'right', 'bottom', 'left'].includes(value);
      },
    },
    /**
     * Hook function before switching tab. If `false` is returned or a `Promise` is returned and then is rejected, switching will be prevented.
     */
    beforeLeave: {
      type: Function,
      default: () => {},
    },
    /**
     * Sticky tab labels stay fixed to the top of the screen while scrolling.
     */
    isSticky: {
      type: Boolean,
      default: false,
    },
    /**
     * Enable lazy loaded tab content.
     */
    isLazy: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      allPanes: [],
      paneIndex: 0,
      activeTabName: this.modelValue || null,
      initialModelValue: null,
      navWidth: 0,
      isInit: true,
    };
  },
  emits: ['update:modelValue'],
  computed: {
    panes() {
      return this.allPanes.filter((tab) => !tab.isRemoved);
    },
    stickyClass() {
      return {'is-sticky': this.isSticky && ['top', 'bottom'].indexOf(this.tabPosition) !== -1};
    },
  },
  watch: {
    modelValue(value) {
      if (!this.panes.some((tab) => tab.tabName === value)) {
        return;
      }

      this.activeTabName = value;
    },
    panes(value) {
      if (!value.length) return;
      if (this.isInit) {
        this.switchTab(this.modelValue || value[0].tabName);
      }
    },
  },
  mounted() {
    if (this.$refs.nav) {
      resizeObserver = new ResizeObserver(() => {
        this.navWidth = this.$refs.nav.clientWidth;
      });
      resizeObserver.observe(this.$refs.nav);
    }
    this.initialModelValue = this.modelValue;
  },
  unmounted() {
    if (!this.$refs.nav) return;
    resizeObserver?.unobserve(this.$refs.nav);
  },
  methods: {
    handleTabClick(tab) {
      this.isInit = false;
      if (tab.disabled) return;
    },
    setInitialOrFirstTabActive() {
      let newActiveTab;
      const initialModelValueIndex = this.panes.findIndex((tab) => tab.tabName === this.initialModelValue);
      if (initialModelValueIndex > -1) {
        newActiveTab = this.panes[initialModelValueIndex].tabName;
      } else {
        newActiveTab = this.panes[0]?.tabName || '';
      }
      return newActiveTab;
    },
    switchTab(updatedTabName) {
      const updateActiveTabName = () => {
        this.activeTabName = updatedTabName;
        if (this.panes.some((tab) => tab.tabName === updatedTabName)) {
          this.$emit('update:modelValue', updatedTabName);
        }
      };

      if (this.activeTabName !== updatedTabName && this.beforeLeave instanceof Function) {
        const leaveResult = this.beforeLeave(updatedTabName, this.currentName);

        if (leaveResult instanceof Promise) {
          leaveResult.then(() => {
            updateActiveTabName();
            this.$refs.nav?.removeFocus();
          });
        } else if (leaveResult !== false) {
          updateActiveTabName();
        }
      } else {
        updateActiveTabName();
      }
    },
  },
};
</script>

<style lang="scss">
@import 'Tabs.scss';
</style>
